/**
 * Wire types for the identity service's verification module
 * (campusone-identity-service src/verification/*), reached through the identity
 * BFF. String-literal unions mirror the server enums in
 * src/common/enums/index.ts verbatim — the server is the source of truth, and
 * unknown values are kept as strings by the normalisers rather than dropped so
 * a newly added enum member degrades to a raw code on screen, not to a crash.
 */

export const CREDENTIAL_TYPES = ['PHYSICAL_CARD', 'MOBILE_IDENTITY', 'TEMPORARY_CARD'] as const;
export type CredentialType = (typeof CREDENTIAL_TYPES)[number];

export const CREDENTIAL_STATUSES = ['ACTIVE', 'SUSPENDED', 'REVOKED', 'EXPIRED', 'REPLACED'] as const;
export type CredentialStatus = (typeof CREDENTIAL_STATUSES)[number];

export const VERIFICATION_PURPOSES = [
  'IDENTITY_CHECK',
  'SCHOOL_ARRIVAL',
  'CLASS_ATTENDANCE',
  'LIBRARY',
  'BUS_BOARDING',
  'BUS_DROPOFF',
  'EXAM_CHECKIN',
  'EVENT_ACCESS',
] as const;
export type VerificationPurpose = (typeof VERIFICATION_PURPOSES)[number];

export const JOURNEY_EVENT_TYPES = [
  'ARRIVAL',
  'CLASS_ATTENDANCE',
  'LIBRARY',
  'BUS_BOARDING',
  'BUS_DROPOFF',
  'EXAM_CHECKIN',
  'EVENT_ACCESS',
  'IDENTITY_CHECK',
] as const;
export type JourneyEventType = (typeof JOURNEY_EVENT_TYPES)[number];

/**
 * The full failure vocabulary from VerificationFailureCode — wider than the
 * table in docs/mobile/identity-verification-api.md, which omits the last four.
 */
export const VERIFICATION_FAILURE_CODES = [
  'CREDENTIAL_NOT_FOUND',
  'CREDENTIAL_SUSPENDED',
  'CREDENTIAL_REVOKED',
  'CREDENTIAL_EXPIRED',
  'CREDENTIAL_REPLACED',
  'QR_INVALID',
  'QR_EXPIRED',
  'QR_REPLAYED',
  'WRONG_INSTITUTION',
  'WRONG_TENANT',
  'DEVICE_NOT_AUTHORIZED',
  'DEVICE_SUSPENDED',
  'DEVICE_REVOKED',
  'PURPOSE_NOT_ALLOWED',
  'PERSON_NOT_ACTIVE',
  'NO_ACTIVE_CONTEXT',
  'DUPLICATE_SCAN',
  'ATTENDANCE_INTEGRATION_FAILED',
] as const;
export type VerificationFailureCode = (typeof VERIFICATION_FAILURE_CODES)[number];

/**
 * One credential row. The list endpoint projects a subset (no personId); the
 * lifecycle actions return the full entity. Fields absent on the wire arrive
 * here as null so callers need no per-endpoint types.
 */
export interface VerificationCredential {
  id: string;
  credentialPublicId: string;
  credentialType: CredentialType | string;
  status: CredentialStatus | string;
  issuedAt: string;
  expiresAt: string | null;
  personId: string | null;
  metadata: Record<string, unknown>;
}

/** POST /verification/verifications — one shape for success AND refusal (HTTP 200 both ways). */
export interface VerificationOutcome {
  result: 'SUCCESS' | 'FAILURE';
  failureCode?: VerificationFailureCode | string;
  message?: string;
  verificationId?: string;
  occurredAt?: string;
  purpose?: VerificationPurpose | string;
  duplicate?: boolean;
  person?: {
    personId: string;
    firstName: string;
    lastName: string;
    displayName: string;
    studentReference?: string;
  };
  journeyEvent?: { eventId: string; eventType: JourneyEventType | string; occurredAt: string };
  attendance?: { recorded: boolean; attendanceId?: string };
}

/** GET /verification/verifications/recent — raw IdentityVerification entities. */
export interface RecentVerification {
  id: string;
  occurredAt: string;
  purpose: VerificationPurpose | string;
  result: 'SUCCESS' | 'FAILURE' | string;
  failureCode: string | null;
  personId: string | null;
  verifiedBy: string | null;
  context: Record<string, unknown>;
}

/** GET /verification/persons/:personId/timeline */
export interface JourneyTimelineEvent {
  eventId: string;
  eventType: JourneyEventType | string;
  occurredAt: string;
  context: Record<string, unknown>;
  externalEventId: string | null;
}

/**
 * GET /verification/credentials/public/:credentialPublicId — being added in a
 * parallel backend lane; this app codes against the agreed shape and treats a
 * 404 as "not deployed yet", never as a broken card.
 */
export interface PublicCredential {
  credentialPublicId: string;
  status: CredentialStatus | string;
  credentialType: CredentialType | string;
  person: {
    displayName: string;
    photographUrl: string | null;
    personStatus: string;
  };
  expiresAt: string | null;
}
